import React, { useEffect, useRef, useState, useCallback } from 'react'
// eslint-disable-next-line import/no-webpack-loader-syntax
import videojs from '!video.js'
import 'video.js/dist/video-js.css'
import styled from 'styled-components'
import muted from './images/muted.png'
import unmuted from './images/unmuted.png'
import play from './images/play.png'
import pause from './images/pause.png'
import { fireUIRenderEvent } from 'src/modules/analytics/v3'
import { useSelector } from 'react-redux'
import { selectStateForProtobuf } from 'src/modules/reducers'
import { selectAnalyticsReady } from 'src/modules/reducers/analytics'

import {
  body,
  colors,
  heading,
  mediaQuery,
  spacing,
  typographyMixin,
} from 'src/constants/marketing-ds'
import CTAButton from '../CTAButtonNew'

const FuboLivePreviewWidget = ({
  callsign,
  ctaHeadline,
  ctaSubhead,
  ctaButton,
  ctaText,
  previewTimerTemplate,
  ctaTrackingParams,
  previewLen,
  signupPath = 'signup',
  autoplay = true,
  loadingText,
  overrideTimer,
  isLoading,
}) => {
  const videoRef = useRef(null)
  const playerRef = useRef(null)
  const timerId = useRef(null)
  const [previewStart, setPreviewStart] = useState(false)
  const [previewEnd, setPreviewEnd] = useState(false)
  const [timer, setTimer] = useState(previewLen)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(true)
  const [isEventSent, setIsEventSent] = useState(false)
  const stateForProtobuf = useSelector(selectStateForProtobuf)
  const isAnalyticsReady = useSelector(selectAnalyticsReady)

  const defaultPreviewLen = 28

  const saveTimer = (time) => {
    localStorage.setItem('fubo_preview_timer', time.toString())
  }

  const getSavedTimer = useCallback(() => {
    const savedTimer = localStorage.getItem('fubo_preview_timer')
    return overrideTimer ? overrideTimer : savedTimer ? parseInt(savedTimer, 10) : defaultPreviewLen
  }, [overrideTimer])

  // We need to check if the previewLen is set but has not been set before
  // because otherwise, the default length will take over
  useEffect(() => {
    const savedPreviewLen = localStorage.getItem('fubo_preview_len')
    if (
      previewLen &&
      previewLen !== null &&
      previewLen !== undefined &&
      previewLen !== parseInt(savedPreviewLen, 10)
    ) {
      localStorage.setItem('fubo_preview_len', previewLen.toString())
      setTimer(previewLen)
    } else {
      setTimer(getSavedTimer())
    }
  }, [previewLen, getSavedTimer])

  const startTimer = () => {
    // Don't start timer if preview has ended
    if (previewEnd) {
      return
    }
    clearInterval(timerId.current)
    timerId.current = setInterval(() => {
      setTimer((prevTimer) => {
        const newTimer = prevTimer <= 1 ? 0 : prevTimer - 1
        saveTimer(newTimer) // Save remaining time

        if (newTimer <= 0) {
          clearInterval(timerId.current)
          setPreviewEnd(true)
          playerRef.current.pause()
        }
        return newTimer
      })
    }, 1000)
  }

  const videoJsOptions = {
    autoplay,
    muted: true,
    controls: true,
    disablePictureInPicture: false,
    preferFullWindow: true,
    playsInline: true,
    sources: [
      {
        src: `https://preview.fubo.tv/${callsign}/1.m3u8`,
        type: 'application/x-mpegURL',
      },
    ],
  }

  const handlePlayPause = useCallback(() => {
    if (playerRef.current) {
      if (playerRef.current.paused()) {
        playerRef.current.play()
      } else {
        playerRef.current.pause()
      }
    }
  }, [])

  const handleMuteToggle = useCallback((event) => {
    event.stopPropagation() // Prevents the event from bubbling up to parent elements
    if (playerRef.current) {
      playerRef.current.muted(!playerRef.current.muted())
    }
  }, [])

  const autoplayRetryLimit = 5 // Set the maximum number of autoplay attempts
  const autoplayRetries = useRef(0)

  const tryToAutoplay = useCallback(
    (player) => {
      // Don't try to autoplay if preview has ended
      if (previewEnd || isLoading) {
        return
      }

      if (autoplayRetries.current >= autoplayRetryLimit) {
        console.error('Autoplay retry limit reached')
        return
      }

      const playPromise = player.play()

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Autoplay started successfully
            setIsPlaying(true)
          })
          .catch((error) => {
            // Autoplay was prevented. Retry autoplay
            console.error('Autoplay failed:', error)
            autoplayRetries.current += 1
            setTimeout(() => tryToAutoplay(player), 1000)
          })
      }
    },
    [previewEnd, isLoading]
  )

  const setupPlayer = () => {
    const videoElement = document.createElement('video-js')
    videoElement.classList.add('vjs-big-play-centered')
    videoElement.setAttribute('playsinline', '')
    videoRef.current.appendChild(videoElement)

    const player = (playerRef.current = videojs(videoElement, videoJsOptions, () => {
      videojs.log('player is ready')

      // Event listeners
      player.on('loadedmetadata', () => {
        tryStartPreview()
      })

      player.on('volumechange', () => {
        setIsMuted(player.muted())
      })
    }))
  }
  const tryStartPreview = useCallback(() => {
    // Use refs to get latest values
    if (!isLoading && !previewStart && !previewEnd && playerRef.current) {
      if (playerRef.current.isReady_) {
        setPreviewStart(true)
        startTimer()
      }
    }
  }, [isLoading, previewStart, previewEnd])
  // Add this new useEffect for autoplay
  useEffect(() => {
    if (autoplay && playerRef.current && previewStart && !previewEnd && !isLoading) {
      tryToAutoplay(playerRef.current)
    }
  }, [autoplay, previewEnd, isLoading, previewStart])

  useEffect(() => {
    if (!isEventSent && previewEnd && isAnalyticsReady) {
      fireUIRenderEvent(
        {
          element: ctaSubhead,
          component: 'live_preview_end_card',
        },
        stateForProtobuf
      )
      setIsEventSent(true)
    }
  }, [isEventSent, previewEnd, isAnalyticsReady])

  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.on('pause', () => {
        setIsPlaying(false)
        clearInterval(timerId.current)
        saveTimer(timer) // Save remaining time on pause
      })
    }
  }, [timer])

  useEffect(() => {
    // Only setup the player if callsign is truthy
    if (callsign && !previewEnd) {
      if (!playerRef.current) {
        setupPlayer()
      } else {
        const player = playerRef.current
        player.autoplay(videoJsOptions.autoplay)
        player.src(videoJsOptions.sources)
      }
    }
  }, [callsign, previewEnd])

  useEffect(() => {
    return () => {
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose()
        playerRef.current = null
      }
      clearInterval(timerId.current)
    }
  }, [])

  // Add this new useEffect to re-setup the play event listener
  useEffect(() => {
    if (playerRef.current) {
      const player = playerRef.current

      // Remove the old play event listener
      player.off('play')

      // Add the new play event listener with current state values
      player.on('play', () => {
        if (previewStart && !previewEnd) {
          setIsPlaying(true)
          startTimer()
        }
      })
    }
  }, [previewStart, previewEnd])

  // Simple fix: when isLoading becomes false, try to start preview
  useEffect(() => {
    if (!isLoading) {
      tryStartPreview()
    }
  }, [isLoading])

  return (
    <StyledLivePreviewPlayer className="live-preview-player">
      {!previewEnd && <div ref={videoRef} className="fubo-livepreview-widget" data-vjs-player />}
      {previewEnd && (
        <div className="preview-cta">
          <h3>{ctaHeadline}</h3>
          <h3>{ctaSubhead}</h3>
          <div className="button-container">
            <CTAButton
              target="_top"
              isOutlined={true}
              link={`https://www.fubo.tv/${signupPath}?${ctaTrackingParams}`}
            >
              {ctaText}
            </CTAButton>
          </div>
        </div>
      )}
      {!previewStart && !previewEnd && (
        <div className="live-player-placeholder">{loadingText || 'Loading...'}</div>
      )}

      {previewStart && !previewEnd && (
        <div className="fubo-widget-wrapper" onClick={handlePlayPause}>
          <div className="fubo-widget">
            <div className="top-nag">
              <h4 className="preview-seconds">{previewTimerTemplate.replace('{time}', timer)}</h4>
              <h4 className="buy-link">
                <span>
                  <a
                    action="sign-up"
                    className="sign-up-cta-header"
                    target="_top"
                    href="https://www.fubo.tv/signup?comp=end_previews"
                  >
                    {ctaText}
                  </a>
                </span>
              </h4>
            </div>
            <div className="controls">
              <div className={`play-pause-button ${isPlaying ? 'playing' : 'paused'}`}></div>
              <div
                className={`mute-button ${isMuted ? 'muted' : ''}`}
                onClick={handleMuteToggle}
              ></div>
            </div>
          </div>
        </div>
      )}
    </StyledLivePreviewPlayer>
  )
}

export default FuboLivePreviewWidget

const StyledLivePreviewPlayer = styled.div`
  width: 100%;
  padding-bottom: 56.25%;
  ${'' /* aspect-ratio: 16/9; */}
  position: relative;
  background: #000;

  .live-player-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    color: #fff;
    z-index: 99;
  }

  .fubo-livepreview-widget {
    color: #fff;
    width: 100%;
    height: 100%;
    position: absolute;

    .video-js {
      width: 100%;
      height: 100%;
    }
  }

  .preview-cta {
    background: ${colors.midnight.medium};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    color: #fff;
    z-index: 99;
    text-align: center;

    h3 {
      ${typographyMixin(heading.md)}
      ${mediaQuery.tablet} {
        ${typographyMixin(heading.xl)}
      }
    }
  }

  .top-nag {
    display: flex;
    align-items: start;
    justify-content: space-between;
    flex-wrap: wrap;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.65) 0%, transparent 100%);
    width: 100%;
    box-sizing: border-box;
    padding: 24px;
    h4 {
      ${typographyMixin(body.xs)}
    }
  }

  .vjs-hidden,
  .vjs-big-play-button,
  .vjs-control-bar {
    display: none !important;
  }

  .fubo-widget-wrapper {
    position: absolute !important;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    color: #fff;
    z-index: 99;

    .fubo-widget {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      ${typographyMixin(body.xs)}

      .preview-seconds {
        margin: 0;
        text-align: left;
        ${typographyMixin(body.xs)}
      }

      .top img {
        height: auto;
        width: auto;
        display: initial;
      }

      .buy-link {
        z-index: 1;
        ${typographyMixin(body.xs)}
        a {
          cursor: pointer;
          text-decoration: underline;

          &:hover {
            color: ${colors.slate[100]};
            transition: color 0.15s ease-in-out;
          }
        }
      }

      a {
        color: #fff;
      }
    }

    .controls {
      position: absolute !important;
      width: 100%;
      height: 100%;
      opacity: 1;
      top: 0;
      left: 0;

      &:hover .play-pause-button {
        opacity: 1;
        transition: opacity 0.25s ease-in-out 0s;
      }

      .play-pause-button {
        cursor: pointer;
        width: 80px;
        height: 80px;
        left: 0;
        opacity: 0;
        top: 50%;
        -webkit-transform: translateY(-50%);
        transform: translateY(-50%);
        margin-left: auto;
        margin-right: auto;
        right: 0;
        bottom: 0;
        position: absolute !important;
        background: url(${play}) no-repeat center center;
        background-size: contain;

        &:hover {
          opacity: 0.8;
        }

        &.paused {
          opacity: 1 !important;
          background: url(${pause}) no-repeat center center;
          background-size: contain;

          ${
            '' /* @media (pointer: coarse) {
            display: block;
          } */
          }
        }

        ${
          '' /* @media (pointer: coarse) {
          &.paused {
            display: none;
          }
        } */
        }
      }

      .mute-button {
        cursor: pointer;
        width: 3%;
        height: 3%;
        max-width: 50px;
        min-width: 25px;
        max-height: 50px;
        min-height: 25px;
        position: absolute !important;
        right: 20px;
        bottom: 20px;
        background: url(${unmuted}) no-repeat center center;
        background-size: contain;

        &.muted {
          background: url(${muted}) no-repeat center center;
          background-size: contain;
        }
      }
    }
  }
  .button-container {
    margin-top: ${spacing.lg};
  }
`
